import {
  Alert,
  Avatar,
  Button,
  Card,
  Checkbox,
  Container,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { UploadParticipantsButton } from './UploadParticipantsButton';
import { useGetPickemParticipantsQuery } from '@/api/pickems/getPickemParticipants';
import { useAppSearchParams } from '@/hooks/useAppSearchParams';
import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import Iconify from '@/components/iconify';
import Scrollbar from '@/components/scrollbar';
// import { UserListToolbar } from '@/sections/@dashboard/user/UserListToolbar';
import { UserListHead } from '@/sections/@dashboard/user/UserListHead';
import { applySortFilter, getComparator, TABLE_HEAD } from './helpers';
import { UserType } from '@/@types/api.types';

export const ParticipantsSection = () => {
  const { pickemId } = useAppSearchParams();

  const {
    data: users = [],
    isLoading: isGetUsersLoading,
    isError: isGetUsersError,
    error: getPickemParticipantsError,
  } = useGetPickemParticipantsQuery(pickemId);

  const [open, setOpen] = useState<HTMLElement | null>(null);

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [selected, setSelected] = useState<UserType[]>([]);

  const [orderBy, setOrderBy] = useState('name');

  const [filterName] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(users);
      return;
    }
    setSelected([]);
  };

  const handleClick = (
    _event: React.MouseEvent<unknown> | React.ChangeEvent<HTMLInputElement>,
    user: UserType
  ) => {
    const selectedIndex = selected.indexOf(user);
    let newSelected: UserType[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, user);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  // const handleFilterByName = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setPage(0);
  //   setFilterName(event.target.value);
  // };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - users.length) : 0;

  const filteredUsers = applySortFilter(
    users,
    getComparator(order, orderBy),
    filterName
  );

  const isNotFound = !filteredUsers.length && !!filterName;

  return (
    <>
      <Helmet>
        <title> Participants | Minimal UI </title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Participants
          </Typography>

          <Stack direction='row' spacing={2}>
            <Button
              variant='contained'
              startIcon={<Iconify icon='eva:plus-fill' />}
              disabled={true}
            >
              New Participant
            </Button>

            <UploadParticipantsButton />
          </Stack>
        </Stack>

        <Card>
          {/* <UserListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
          /> */}

          {isGetUsersLoading ? (
            <Skeleton height={200} />
          ) : isGetUsersError ? (
            <Alert severity='error' sx={{ m: 2 }}>
              Error loading users: {getPickemParticipantsError?.message}
            </Alert>
          ) : (
            <>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }}>
                  <Table>
                    <UserListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={TABLE_HEAD}
                      rowCount={users.length}
                      numSelected={selected.length}
                      onRequestSort={handleRequestSort}
                      onSelectAllClick={handleSelectAllClick}
                    />
                    <TableBody>
                      {filteredUsers
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((row) => {
                          const { user_id, sso_user_id = '', username } = row;
                          const selectedUser = selected.indexOf(user_id) !== -1;

                          return (
                            <TableRow
                              hover
                              key={user_id}
                              tabIndex={-1}
                              role='checkbox'
                              selected={selectedUser}
                            >
                              <TableCell padding='checkbox'>
                                <Checkbox
                                  checked={selectedUser}
                                  onChange={(event) => handleClick(event, row)}
                                />
                              </TableCell>

                              <TableCell>
                                <Typography
                                  variant='subtitle2'
                                  noWrap
                                  width='40px'
                                >
                                  {user_id}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Typography variant='subtitle2' noWrap>
                                  {sso_user_id}
                                </Typography>
                              </TableCell>

                              <TableCell scope='row' padding='none'>
                                <Stack
                                  direction='row'
                                  alignItems='center'
                                  spacing={2}
                                >
                                  <Avatar alt={username} src={''} />
                                  <Typography variant='subtitle2' noWrap>
                                    {username}
                                  </Typography>
                                </Stack>
                              </TableCell>

                              {/* <TableCell align='left'>{company}</TableCell> */}

                              {/* <TableCell align='left'>{role}</TableCell> */}

                              {/* <TableCell align='left'>
                                {isVerified ? 'Yes' : 'No'}
                              </TableCell> */}

                              {/* <TableCell align='left'>
                                <Label
                                  color={
                                    (status === 'banned' && 'error') ||
                                    'success'
                                  }
                                >
                                  {sentenceCase(status)}
                                </Label>
                              </TableCell> */}

                              <TableCell align='right'>
                                <IconButton
                                  size='large'
                                  color='inherit'
                                  onClick={handleOpenMenu}
                                  disabled={true}
                                >
                                  <Iconify icon={'eva:more-vertical-fill'} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>

                    {isNotFound && (
                      <TableBody>
                        <TableRow>
                          <TableCell align='center' colSpan={6} sx={{ py: 3 }}>
                            <Paper
                              sx={{
                                textAlign: 'center',
                              }}
                            >
                              <Typography variant='h6' paragraph>
                                Not found
                              </Typography>

                              <Typography variant='body2'>
                                No results found for &nbsp;
                                <strong>&quot;{filterName}&quot;</strong>.
                                <br /> Try checking for typos or using complete
                                words.
                              </Typography>
                            </Paper>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Scrollbar>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component='div'
                count={users.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>
    </>
  );
};
